/**
 * DviStageCapture - Stage 1: Media capture and gallery
 * 
 * Upload photos/videos and manage the media gallery with batch actions.
 */

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, Camera } from 'lucide-react';
import { DviMediaGallery } from '../DviMediaGallery';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { runOcrAndVisionPipeline } from '@/lib/dvi/runOcrAndVisionPipeline';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import type { DviMediaItem } from '@/hooks/useDviMedia';

interface DviStageCaptureProps {
  ticketId: string;
  shopId: string;
  sessionId: string | null;
  userId: string | null;
  media: DviMediaItem[];
  ungroupedCount: number;
  groupedCount: number;
  excludedCount: number;
  onToggleExclude: (mediaId: string, excluded: boolean) => Promise<void>;
  onDelete: (mediaId: string) => Promise<void>;
  onCreateSession: () => Promise<{ session_id: string } | undefined>;
  canUpload: boolean;
  canDelete: boolean;
  isLoading: boolean;
  isDeleting: boolean;
  isToggling: boolean;
}

export function DviStageCapture({
  ticketId,
  shopId,
  sessionId,
  userId,
  media,
  ungroupedCount,
  groupedCount,
  excludedCount,
  onToggleExclude,
  onDelete,
  onCreateSession,
  canUpload,
  canDelete,
  isLoading,
  isDeleting,
  isToggling,
}: DviStageCaptureProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const learningEnabled = useFeatureFlag('ui_dviLearning');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    // Clear input for re-selection
    event.target.value = '';
    setIsUploading(true);

    try {
      // Ensure session exists
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const created = await onCreateSession();
        currentSessionId = created?.session_id ?? null;
      }

      if (!currentSessionId) {
        throw new Error('Unable to create or load a DVI session');
      }

      let successCount = 0;
      let failCount = 0;

      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (!isImage && !isVideo) {
          failCount += 1;
          continue;
        }

        try {
          const uuid = crypto.randomUUID();
          const ext = file.name.split('.').pop() || (isImage ? 'jpg' : 'mp4');
          const storagePath = `${shopId}/${ticketId}/${uuid}.${ext}`;

          const { error: uploadError } = await supabase.storage
            .from('dvi_media')
            .upload(storagePath, file, { upsert: false });

          if (uploadError) throw uploadError;

          const { data: insertedMedia, error: insertError } = await supabase
            .from('ticket_dvi_media')
            .insert({
              shop_id: shopId,
              ticket_id: ticketId,
              session_id: currentSessionId,
              storage_path: storagePath,
              media_type: isImage ? 'image' : 'video',
              captured_by_user_id: userId,
              created_by_user_id: userId,
            })
            .select('id')
            .single();

          if (insertError) throw insertError;
          successCount += 1;

          // Phase 13: fire-and-forget OCR + vision extraction
          if (learningEnabled && isImage && insertedMedia?.id) {
            void runOcrAndVisionPipeline({
              shopId,
              ticketId,
              mediaId: insertedMedia.id,
            });
          }
        } catch (err) {
          console.warn('[DVI Upload] File failed:', file.name, err);
          failCount += 1;
        }
      }

      // Refresh queries
      queryClient.invalidateQueries({ queryKey: ['dvi-media', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-media-count', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['dvi-media-ungrouped', ticketId] });

      if (successCount > 0 && failCount === 0) {
        toast({ title: 'Uploaded', description: `${successCount} file(s) uploaded` });
      } else if (successCount > 0 && failCount > 0) {
        toast({
          title: 'Partial upload',
          description: `${successCount} uploaded, ${failCount} failed`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Upload failed',
          description: 'No valid files were uploaded',
          variant: 'destructive',
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      toast({ title: 'Upload failed', description: message, variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Controls */}
      {canUpload && (
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.setAttribute('capture', 'environment');
                fileInputRef.current.click();
              }
            }}
            disabled={isUploading}
          >
            <Camera className="h-4 w-4" />
            Camera
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
        </div>
      )}

      {/* Media Gallery */}
      <DviMediaGallery
        media={media}
        ungroupedCount={ungroupedCount}
        groupedCount={groupedCount}
        excludedCount={excludedCount}
        onToggleExclude={onToggleExclude}
        onDelete={onDelete}
        canDelete={canDelete}
        isLoading={isLoading}
        isDeleting={isDeleting}
        isToggling={isToggling}
      />
    </div>
  );
}
